import { Model } from '@adobe/aem-spa-page-model-manager';
import { produce } from 'immer';
import throttle from 'lodash/throttle';
import { StateCreator } from 'zustand';
import { createAppStore } from '../zustand';
import { EXPERIENCE_FRAGMENT_TYPE } from './constants';
import { getNonStickyTop, getStickySearchFormLayoutBreakpoint } from './stickySearchFormLayoutOffsets';

export enum ScrollDirection {
  UP = 'up',
  DOWN = 'down',
  INDETERMINATE = '',
}

export interface ScrollSettings {
  isNavComponent?: boolean;
  isStickyOnScrollDown?: boolean;
  isStickyOnScrollUp?: boolean;
}
export interface ScrollStoreModel extends Model {
  componentId: string;
  scrollSetting?: ScrollSettings;
  ':type'?: string;
  ':itemsOrder'?: string[];
  ':children'?: {
    [key: string]: ScrollStoreModel;
  };
  ':items'?: {
    [key: string]: ScrollStoreModel;
  };
}
export interface RegisteredComponent {
  componentId: string;
  isStickyOnScrollUp: boolean;
  isStickyOnScrollDown: boolean;
  height: number;
  topOffset?: number;
  /** When set, sticky scroll threshold uses {@link getNonStickyTop} + viewport (Polaris vs BAU / global nav). */
  stickyScrollThresholdLayout?: {
    isNorthStar?: boolean;
    hasGlobalNav?: boolean;
  };
}
export interface ScrollState {
  scrollPosition: number;
  prevScrollPosition: number;
  bodyMargin: number;
  scrollDirection: ScrollDirection;
  prevScrollDirection: ScrollDirection;
  navComponentOrder: string[];
  registeredComponents: {
    [key: RegisteredComponent['componentId']]: RegisteredComponent & {
      topOffset: number;
      itemsOrderIndex: number;
    };
  };
  bodyObserver: MutationObserver | null;
  initialize: (data: Model) => void;
  destroy: () => void;
  handleScroll: () => void;
  handleBodyMutation: () => void;
  setNavComponentOrder: (data: Model) => void;
  setRegisteredComponent: (component: RegisteredComponent) => void;
}

const initialState = {
  scrollPosition: 0,
  prevScrollPosition: 0,
  scrollDirection: ScrollDirection.INDETERMINATE,
  prevScrollDirection: ScrollDirection.INDETERMINATE,
  navComponentOrder: [],
  registeredComponents: {},
  bodyObserver: null,
  bodyMargin: 0,
};

/**
 * Collects nav component IDs from a single tree node (grid child, EF, or leaf).
 * Structural containers with :items + :itemsOrder (e.g. dcaabtestwrapper, layout container)
 * are flattened so nested isNavComponent entries participate in scroll order.
 */
const collectNavIdsFromTreeNode = (node: ScrollStoreModel | undefined): string[] => {
  if (!node) {
    return [];
  }

  const nodeType = node[':type'] as string | undefined;

  if (nodeType === EXPERIENCE_FRAGMENT_TYPE) {
    const responsiveGrid = node?.[':items']?.['root']?.[':items']?.['responsivegrid'] as ScrollStoreModel;
    return getNavComponentOrder(responsiveGrid);
  }

  if (node.scrollSetting?.isNavComponent) {
    const id = node['componentId' as keyof Model] as string;
    return id ? [id] : [];
  }

  if (node[':itemsOrder']?.length && node[':items']) {
    return getNavComponentOrder(node);
  }

  return [];
};

/**
 * Returns component IDs in DOM/AEM order for sticky stacking, including:
 * - Nav components inside experience fragments
 * - Nav components nested under structural wrappers (DCA A/B wrapper, containers, etc.)
 */
const getNavComponentOrder = (data: ScrollStoreModel): string[] => {
  if (!data) {
    return [];
  }
  const itemsOrder = data[':itemsOrder'];
  const items = data[':items'];
  if (!itemsOrder?.length || !items) {
    return [];
  }

  return itemsOrder.reduce((order: string[], key: string) => {
    const child = items[key] as ScrollStoreModel;
    return order.concat(collectNavIdsFromTreeNode(child));
  }, []);
};

export const clientScrollStore: StateCreator<ScrollState> = (set, get) => {
  let lastScrollPosition = 0;
  let lastScrollDirection = ScrollDirection.INDETERMINATE;
  let idleScrollTimer: ReturnType<typeof setTimeout> | null = null;
  const EDGE_TOLERANCE_PX = 2;

  const setScrollState = throttle((scrollY: number, direction: ScrollDirection) => {
    set(state => ({
      prevScrollPosition: state.scrollPosition,
      scrollPosition: scrollY,
      prevScrollDirection: direction === ScrollDirection.INDETERMINATE ? lastScrollDirection : state.scrollDirection,
      scrollDirection: direction,
    }));
  }, 100);

  return {
    ...initialState,
    initialize: (data: Model) => {
      if (!data) {
        return;
      }

      const { setNavComponentOrder, handleScroll, handleBodyMutation } = get();
      setNavComponentOrder(data);

      // if we're server-side or there are no nav components, there's nothing to do
      if (typeof document === 'undefined' || get().navComponentOrder.length === 0) {
        return;
      }

      document.addEventListener('scroll', handleScroll);
      handleScroll();

      // the Branch.io app banner adds a margin-top to the body, so we can use that instead of
      // wiring up branch-specific callbacks and risk 3rd-party library race conditions
      const bodyObserver = new MutationObserver(handleBodyMutation);
      bodyObserver.observe(document.body, { attributes: true });
      set({ bodyObserver });
      handleBodyMutation();
    },
    destroy: () => {
      document.removeEventListener('scroll', get().handleScroll);
      get().bodyObserver?.disconnect();
      if (idleScrollTimer) {
        clearTimeout(idleScrollTimer);
        idleScrollTimer = null;
      }
      setScrollState.cancel();
    },
    handleScroll: () => {
      const currentScrollY = window.scrollY;
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const hasScrollablePage = maxScrollY > EDGE_TOLERANCE_PX;
      const isAtBottom = hasScrollablePage && currentScrollY >= maxScrollY - EDGE_TOLERANCE_PX;

      // Ignore downward/idle wheel events at the bottom edge to avoid sticky flicker.
      if (isAtBottom && currentScrollY >= lastScrollPosition) {
        lastScrollPosition = currentScrollY;
        return;
      }

      if (currentScrollY > lastScrollPosition) {
        lastScrollDirection = ScrollDirection.DOWN;
      } else if (currentScrollY < lastScrollPosition) {
        lastScrollDirection = ScrollDirection.UP;
      } else {
        return;
      }
      lastScrollPosition = currentScrollY;
      if (idleScrollTimer) {
        clearTimeout(idleScrollTimer);
      }
      setScrollState(currentScrollY, lastScrollDirection);
      idleScrollTimer = setTimeout(() => {
        setScrollState(lastScrollPosition, ScrollDirection.INDETERMINATE);
      }, 120);
    },
    handleBodyMutation: () => {
      set({ bodyMargin: parseInt(document.body.style.marginTop) || 0 });
    },
    setNavComponentOrder: (data: Model) => {
      if (!data) {
        return;
      }
      set({ navComponentOrder: getNavComponentOrder(data as ScrollStoreModel) });
    },
    setRegisteredComponent: (component: RegisteredComponent) => {
      const { navComponentOrder } = get();

      // if the store hasn't been instantiated do nothing.
      if (!navComponentOrder.length) {
        return;
      }

      set(
        produce(state => {
          const prev = state.registeredComponents[component.componentId];
          // Prefer lastIndexOf: the same componentId can appear more than once in
          // nav order (e.g. experience fragment + main grid, or two authored instances).
          // The mounted node registers last and should use the last DOM/AEM list position
          // so preceding-nav height offsets match useStickyState stacking.
          state.registeredComponents[component.componentId] = {
            ...(prev || {}),
            ...component,
            topOffset: component.topOffset ?? prev?.topOffset ?? 0,
            itemsOrderIndex: navComponentOrder.lastIndexOf(component.componentId),
          };
        })
      );
    },
  };
};

export const useScrollStore = createAppStore(clientScrollStore, { usePersistentStore: false });

export const useStickyState = (componentId: string) => {
  const registeredComponents = useScrollStore(state => state.registeredComponents);
  const scrollPosition = useScrollStore(state => state.scrollPosition);
  const scrollDirection = useScrollStore(state => state.scrollDirection);
  const prevScrollDirection = useScrollStore(state => state.prevScrollDirection);
  const bodyMargin = useScrollStore(state => state.bodyMargin);
  const component = registeredComponents[componentId];
  const isScrolled = scrollPosition > 0;
  const { UP, DOWN, INDETERMINATE } = ScrollDirection;
  const stickyState = { isSticky: false, offset: bodyMargin, top: 0 };

  if (!component || typeof window === 'undefined') {
    return stickyState;
  }

  const { itemsOrderIndex, isStickyOnScrollDown, isStickyOnScrollUp } = component;

  // if we can't determine a scroll direction (rerender without scrolling),
  // use the previous direction
  const isScrolling = (dir: ScrollDirection) =>
    scrollDirection === INDETERMINATE ? isScrolled && prevScrollDirection === dir : scrollDirection === dir;
  const isScrollingDown = isScrolling(DOWN) && isStickyOnScrollDown;
  const isScrollingUp = isScrolling(UP) && isStickyOnScrollUp;

  // for all components "previous" to the given component in the order,
  // add the offset and top position based on the scroll direction
  // (ignore itemsOrderIndex -1: not present in nav order — avoids corrupting header math)
  let cumulativeOffset = bodyMargin;
  Object.values(registeredComponents)
    .filter(c => c && c.itemsOrderIndex >= 0 && c.itemsOrderIndex < itemsOrderIndex)
    .forEach(c => {
      cumulativeOffset += c.height;
      if ((isScrollingDown && c.isStickyOnScrollDown) || (isScrollingUp && c.isStickyOnScrollUp)) {
        stickyState.top += c.height;
      }
    });

  const layoutMeta = component.stickyScrollThresholdLayout;
  const thresholdOffset =
    layoutMeta != null
      ? bodyMargin +
        getNonStickyTop(getStickySearchFormLayoutBreakpoint(), layoutMeta.isNorthStar, layoutMeta.hasGlobalNav)
      : cumulativeOffset;

  stickyState.offset = thresholdOffset;

  stickyState.isSticky =
    (isScrollingDown && scrollPosition > thresholdOffset + component.topOffset) ||
    (isScrollingUp && scrollPosition > thresholdOffset - component.topOffset);

  return stickyState;
};
